import { useState, useEffect } from "react";
import { 
  ArrowRight, 
  Menu, 
  X, 
  Shield, 
  FileCheck, 
  Bot, 
  CheckCircle2, 
  Clock, 
  DollarSign,
  Star,
  Users,
  Building2,
  Sparkles,
  ChevronRight,
  Download
} from "lucide-react";
import { 
  IconCleaning,
  IconGarden,
  IconPool,
  IconAirConditioner,
  IconRenovation,
  IconConstruction,
  IconShield
} from "../components/MinimalIcons";

// High quality images - each unique
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?auto=format&fit=crop&w=2000&q=80",
  pool: "https://images.unsplash.com/photo-1535205148555-bcbbc2a78913?auto=format&fit=crop&w=1200&q=80",
  garden: "https://images.unsplash.com/photo-1734303023491-db8037a21f09?auto=format&fit=crop&w=1200&q=80",
  cleaning: "https://images.unsplash.com/photo-1763485955499-f11dcfdda823?auto=format&fit=crop&w=1200&q=80",
  aircon: "https://images.unsplash.com/photo-1757219525975-03b5984bc6e8?auto=format&fit=crop&w=1200&q=80",
  renovation: "https://images.unsplash.com/photo-1682888818650-0a7cbb35287d?auto=format&fit=crop&w=1200&q=80",
  construction: "https://images.unsplash.com/photo-1639380366818-6053eb8f174c?auto=format&fit=crop&w=1200&q=80",
  worker: "https://images.unsplash.com/photo-1622904341310-391c07a25e39?auto=format&fit=crop&w=1200&q=80"
};

const SERVICES = [
  { 
    id: "cat_diarista", 
    name: "Limpeza Residencial", 
    desc: "Diaristas verificadas para manter sua casa impecável",
    Icon: IconCleaning,
    image: IMAGES.cleaning
  },
  { 
    id: "cat_jardinagem", 
    name: "Jardinagem", 
    desc: "Paisagismo e manutenção de jardins por especialistas",
    Icon: IconGarden,
    image: IMAGES.garden
  },
  { 
    id: "cat_piscina", 
    name: "Piscina", 
    desc: "Limpeza, tratamento químico e manutenção completa",
    Icon: IconPool,
    image: IMAGES.pool
  },
  { 
    id: "cat_arcondicionado", 
    name: "Ar-Condicionado", 
    desc: "Instalação, limpeza e manutenção de climatização",
    Icon: IconAirConditioner,
    image: IMAGES.aircon
  },
  { 
    id: "cat_reforma", 
    name: "Reforma", 
    desc: "Banheiro, cozinha e pequenas obras com acompanhamento",
    Icon: IconRenovation,
    image: IMAGES.renovation
  },
  { 
    id: "cat_construcao", 
    name: "Construção", 
    desc: "Construção residencial completa com cronograma",
    Icon: IconConstruction,
    image: IMAGES.construction
  }
];

const STATS = [
  { value: "50K+", label: "Serviços realizados", icon: CheckCircle2 },
  { value: "15K+", label: "Profissionais ativos", icon: Users },
  { value: "R$ 2M+", label: "Em Pagamento Protegido", icon: Shield },
  { value: "4.9", label: "Avaliação média", icon: Star }
];

const STEPS = [
  { num: "01", title: "Baixe o App", desc: "Disponível para iOS e Android gratuitamente" },
  { num: "02", title: "Descreva seu projeto", desc: "Nossa IA vai ajudar a detalhar seu pedido" },
  { num: "03", title: "Compare orçamentos", desc: "Receba propostas de profissionais verificados" },
  { num: "04", title: "Contrate com segurança", desc: "Pagamento protegido até você aprovar" }
];

const FEATURES = [
  {
    icon: Shield,
    title: "Pagamento 100% Protegido",
    desc: "Sistema de Conta Garantia: o dinheiro só é liberado após a aprovação do serviço. Zero risco para ambas as partes.",
    highlight: true
  },
  {
    icon: FileCheck,
    title: "Contrato Digital",
    desc: "Contrato gerado automaticamente com validade jurídica. Assinatura digital simples e segura pelo celular."
  },
  {
    icon: Bot,
    title: "Assistente com IA",
    desc: "Inteligência artificial analisa orçamentos, detecta riscos e ajuda na negociação para garantir um acordo justo."
  },
  {
    icon: CheckCircle2,
    title: "Profissionais Verificados",
    desc: "Todos os prestadores passam por verificação de identidade e histórico. Avaliações reais de clientes."
  }
];

const TESTIMONIALS = [
  {
    name: "Marina Costa",
    role: "Cliente",
    text: "Contratei uma reforma de banheiro e pela primeira vez me senti segura. O contrato digital e o pagamento protegido fizeram toda diferença.",
    rating: 5,
    avatar: "MC"
  },
  {
    name: "Carlos Silva",
    role: "Prestador",
    text: "Dobrei meus clientes em 3 meses. A plataforma traz clientes sérios e o pagamento garantido me dá tranquilidade para trabalhar.",
    rating: 5,
    avatar: "CS"
  },
  {
    name: "Ana Paula",
    role: "Cliente",
    text: "A IA identificou que o orçamento estava incompleto e sugeriu incluir itens importantes. Evitou dor de cabeça depois.",
    rating: 5,
    avatar: "AP"
  }
];

// App Store URLs (placeholder - update with real URLs when available)
const APP_STORE_URL = "https://apps.apple.com/app/ezelo";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ezelo";

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToDownload = () => {
    document.getElementById('app')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ========== HEADER ========== */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? "bg-white/95 backdrop-blur-md shadow-sm" 
          : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <a href="/" className="relative z-10">
            <span className={`text-xl sm:text-2xl font-bold tracking-tight transition-colors duration-300 ${
              scrolled ? "text-neutral-900" : "text-white"
            }`}>
              eZelo.
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {[
              { href: "#services", label: "Serviços" },
              { href: "#how-it-works", label: "Como Funciona" },
              { href: "#features", label: "Vantagens" },
              { href: "#professionals", label: "Sou Profissional" },
              { href: "#testimonials", label: "Depoimentos" }
            ].map(item => (
              <a 
                key={item.href}
                href={item.href} 
                className={`text-sm font-medium transition-colors duration-300 hover:opacity-70 ${
                  scrolled ? "text-neutral-600" : "text-white/90"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Navigation only - no download button */}

          {/* Mobile Menu Button */}
          <button 
            className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-full transition-colors ${
              scrolled ? "text-neutral-900" : "text-white"
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden fixed inset-0 top-20 bg-white z-40 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="p-6 space-y-6">
              {[
                { href: "#services", label: "Serviços" },
                { href: "#how-it-works", label: "Como Funciona" },
                { href: "#features", label: "Vantagens" },
                { href: "#professionals", label: "Sou Profissional" },
                { href: "#testimonials", label: "Depoimentos" },
                { href: "#app", label: "Baixar App" }
              ].map(item => (
                <a 
                  key={item.href}
                  href={item.href} 
                  onClick={() => setMenuOpen(false)}
                  className="block text-lg font-medium text-neutral-900"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="Modern luxury home interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 py-20 landscape:py-6">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-md rounded-full mb-5 sm:mb-8 landscape:mb-3 animate-fade-in border border-white/10">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-xs sm:text-sm text-white font-semibold tracking-wide">Plataforma #1 de Serviços Residenciais</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl landscape:text-2xl font-bold text-white leading-[1.1] tracking-tight mb-4 sm:mb-6 landscape:mb-3 animate-fade-in-up">
              Contrate serviços para sua casa com{" "}
              <span className="text-yellow-400 underline decoration-2 underline-offset-4 decoration-yellow-400/50">
                pagamento protegido
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg md:text-xl landscape:text-sm text-white/90 leading-relaxed mb-6 sm:mb-10 landscape:mb-4 max-w-2xl animate-fade-in-up animation-delay-100">
              Seu dinheiro só é liberado após você aprovar o serviço. Contratos digitais com validade jurídica e assistente IA para garantir negociações justas.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 pt-5 sm:pt-8 landscape:pt-3 border-t border-white/20 animate-fade-in-up animation-delay-300">
              {[
                { icon: Shield, text: "Pagamento Protegido" },
                { icon: FileCheck, text: "Contrato Digital" },
                { icon: Bot, text: "Assistente IA" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                  <span className="text-xs sm:text-sm font-semibold text-white">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator - hidden in landscape */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce landscape:hidden">
          <div className="w-8 h-14 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-scroll-down" />
          </div>
        </div>
      </section>

      {/* ========== STATS BAR ========== */}
      <section className="relative z-20 -mt-12 sm:-mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl shadow-neutral-900/10 p-5 sm:p-8 md:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 md:gap-4">
              {STATS.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-neutral-100 mb-2 sm:mb-4">
                    <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-700" />
                  </div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-neutral-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICES SECTION ========== */}
      <section id="services" className="py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-neutral-100 rounded-full text-[10px] sm:text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-3 sm:mb-4">
              Serviços
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-3 sm:mb-4">
              O que você precisa?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
              Encontre profissionais qualificados para qualquer serviço residencial
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((service, i) => {
              const Icon = service.Icon;
              return (
                <div
                  key={service.id}
                  className="group relative overflow-hidden rounded-3xl bg-neutral-50 transition-all duration-500"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/90 via-neutral-900/40 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{service.name}</h3>
                        <p className="text-white/70 text-sm">{service.desc}</p>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* More Services Info */}
          <div className="mt-8 sm:mt-12">
            <div
              className="w-full p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-neutral-900 to-neutral-800 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6"
              data-testid="more-services-banner"
            >
              <div className="flex items-center gap-3 sm:gap-5">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <IconShield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-base sm:text-xl font-bold text-white">E muito mais serviços!</p>
                  <p className="text-white/60 text-xs sm:text-base mt-0.5 sm:mt-1">Baixe o app e encontre profissionais para qualquer necessidade</p>
                </div>
              </div>
              <button 
                onClick={scrollToDownload}
                className="flex items-center gap-2 text-white font-medium hover:gap-4 transition-all text-sm sm:text-base"
              >
                <span>Baixar App</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section id="how-it-works" className="py-16 sm:py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-white rounded-full text-[10px] sm:text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-3 sm:mb-4 shadow-sm">
              Como Funciona
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-3 sm:mb-4">
              Simples, seguro e transparente
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
              Todo o processo pensado para proteger você do início ao fim
            </p>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {STEPS.map((step, i) => (
              <div 
                key={step.num}
                className="relative p-5 sm:p-8 bg-white rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <div className="absolute -top-3 sm:-top-4 left-5 sm:left-8">
                  <span className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-neutral-900 text-white text-xs sm:text-sm font-bold">
                    {step.num}
                  </span>
                </div>
                <div className="pt-3 sm:pt-4">
                  <h3 className="text-base sm:text-xl font-bold text-neutral-900 mb-1 sm:mb-2">{step.title}</h3>
                  <p className="text-neutral-500 text-sm sm:text-base">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 text-neutral-300">
                    <ChevronRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-8 sm:mt-12">
            <button 
              onClick={scrollToDownload}
              className="h-12 sm:h-14 px-6 sm:px-10 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 text-sm sm:text-base font-semibold inline-flex items-center gap-2"
              data-testid="how-it-works-cta"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              Baixar App Grátis
            </button>
          </div>
        </div>
      </section>

      {/* ========== REAL SCENARIOS - PAIN POINTS ========== */}
      <section className="py-16 sm:py-24 md:py-32 bg-neutral-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-20">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full text-xs font-semibold text-white/80 uppercase tracking-wider mb-6">
              Isso já aconteceu com você?
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 sm:mb-6">
              Histórias reais, soluções reais
            </h2>
            <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
              Veja como a eZelo transforma situações frustrantes em experiências tranquilas
            </p>
          </div>

          {/* Scenario 1 - Professional */}
          {/* Scenario 1 - Client (Rafael) */}
          <div className="mb-16 sm:mb-24">
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <span className="px-3 py-1 bg-white/10 border border-white/20 text-white/70 text-[10px] font-semibold rounded-full uppercase tracking-wider">Cliente</span>
            </div>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">R</span>
              </div>
              <div>
                <p className="text-white font-semibold">Rafael</p>
                <p className="text-white/50 text-sm">Reforma da Piscina</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* BEFORE */}
              <div className="relative">
                <div className="absolute -top-3 left-4 z-10">
                  <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">ANTES</span>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 h-full">
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-3 p-3 bg-neutral-800 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">1</div>
                      <div>
                        <p className="text-white text-sm">Acordo verbal</p>
                        <p className="text-white/40 text-xs">"Trago o contrato depois"</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-neutral-800 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">2</div>
                      <div>
                        <p className="text-white text-sm">Pagou 50% antecipado</p>
                        <p className="text-white/40 text-xs">R$ 15.000 via PIX</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/20 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/70">!</div>
                      <div>
                        <p className="text-white/90 text-sm font-medium">90+ dias de atraso</p>
                        <p className="text-white/40 text-xs">Não entregou. Sumiu.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm italic">"Confiei na palavra dele. Meu dinheiro? Perdido."</p>
                </div>
              </div>

              {/* AFTER */}
              <div className="relative">
                <div className="absolute -top-3 left-4 z-10">
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">COM eZelo</span>
                </div>
                <div className="bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/20 rounded-2xl p-5 sm:p-6 h-full">
                  <div className="bg-white rounded-xl p-4 mb-4 shadow-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-neutral-900" />
                      <span className="text-neutral-900 text-sm font-semibold">Contrato Protegido</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-neutral-500 text-sm">Valor total</span>
                        <span className="text-neutral-900 font-bold">R$ 30.000</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <span className="text-emerald-700 text-sm">Conta Garantia</span>
                        <span className="text-emerald-700 font-bold text-sm">R$ 30.000</span>
                      </div>
                      <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-neutral-700 text-sm">Prazo: 30 dias</span>
                          <span className="text-neutral-500 text-xs font-medium">Acompanhando</span>
                        </div>
                        <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                          <div className="h-full w-1/3 bg-neutral-400 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-emerald-300 text-sm italic">"Só libero quando aprovar. Se não entregar, recebo de volta."</p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-16 sm:mb-24">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-sm">E também...</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Scenario 2 - Professional (Cleber) */}
          <div>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <span className="px-3 py-1 bg-white/10 border border-white/20 text-white/70 text-[10px] font-semibold rounded-full uppercase tracking-wider">Profissional</span>
            </div>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">C</span>
              </div>
              <div>
                <p className="text-white font-semibold">Cleber, Jardineiro</p>
                <p className="text-white/50 text-sm">São Paulo, SP</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* BEFORE */}
              <div className="relative">
                <div className="absolute -top-3 left-4 z-10">
                  <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">ANTES</span>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 h-full">
                  <div className="bg-neutral-800 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">W</span>
                      </div>
                      <span className="text-white/80 text-sm font-medium">WhatsApp</span>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-emerald-900/50 rounded-lg rounded-tl-none p-3 max-w-[85%]">
                        <p className="text-white text-sm">Serviço finalizado! ✅ Pode fazer o PIX?</p>
                        <p className="text-white/40 text-[10px] mt-1">14:32</p>
                      </div>
                      <p className="text-white/30 text-xs italic pl-2">3 dias depois...</p>
                      <div className="bg-emerald-900/50 rounded-lg rounded-tl-none p-3 max-w-[85%]">
                        <p className="text-white text-sm">Oi, conseguiu ver? 😅</p>
                        <p className="text-white/40 text-[10px] mt-1">09:15</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm italic">"Fiz o serviço e fiquei na agonia esperando o pagamento..."</p>
                </div>
              </div>

              {/* AFTER */}
              <div className="relative">
                <div className="absolute -top-3 left-4 z-10">
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">COM eZelo</span>
                </div>
                <div className="bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/20 rounded-2xl p-5 sm:p-6 h-full">
                  <div className="bg-white rounded-xl p-4 mb-4 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-neutral-900 text-sm font-semibold">Jardinagem</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded">Concluído</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-neutral-100">
                      <span className="text-neutral-500 text-sm">Valor</span>
                      <span className="text-neutral-900 font-bold">R$ 250</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <span className="text-emerald-700 text-sm font-medium">Cliente aprovou!</span>
                    </div>
                    <div className="mt-3 p-3 bg-emerald-600 rounded-lg text-center">
                      <p className="text-white font-bold text-sm">R$ 250 na sua conta</p>
                    </div>
                  </div>
                  <p className="text-emerald-300 text-sm italic">"Dinheiro garantido desde o início. Sem constrangimento!"</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16 sm:mt-20">
            <p className="text-white/60 text-lg mb-6">
              Não passe por isso. Proteja-se.
            </p>
            <button 
              onClick={scrollToDownload}
              className="h-12 sm:h-14 px-8 sm:px-10 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 text-sm sm:text-base font-semibold inline-flex items-center gap-2 transition-colors"
              data-testid="scenarios-cta"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              Baixar App Grátis
            </button>
          </div>
        </div>
      </section>

      {/* ========== FEATURES / BENEFITS ========== */}
      <section id="features" className="py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left - Content */}
            <div>
              <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-neutral-100 rounded-full text-[10px] sm:text-xs font-semibold text-neutral-600 tracking-wider mb-4 sm:mb-6">
                Por que eZelo.
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-4 sm:mb-6">
                A única plataforma com proteção completa
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-neutral-500 mb-6 sm:mb-10">
                Desenvolvemos um sistema que protege tanto quem contrata quanto quem presta o serviço. Transparência e segurança em cada etapa.
              </p>

              <div className="space-y-3 sm:space-y-6">
                {FEATURES.map((feature, i) => (
                  <div 
                    key={i} 
                    className={`flex gap-3 sm:gap-5 p-3 sm:p-5 rounded-xl sm:rounded-2xl transition-colors ${
                      feature.highlight ? "bg-neutral-900 text-white" : "bg-neutral-50 hover:bg-neutral-100"
                    }`}
                  >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 ${
                      feature.highlight ? "bg-white/10" : "bg-white shadow-sm"
                    }`}>
                      <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${feature.highlight ? "text-white" : "text-neutral-700"}`} />
                    </div>
                    <div>
                      <h3 className={`font-bold text-sm sm:text-lg mb-0.5 sm:mb-1 ${feature.highlight ? "text-white" : "text-neutral-900"}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-xs sm:text-sm ${feature.highlight ? "text-white/70" : "text-neutral-500"}`}>
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative mt-8 lg:mt-0 pt-3">
              {/* Professional Badge */}
              <div className="absolute top-0 left-4 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-lg border border-neutral-100 z-10">
                <span className="text-[10px] font-semibold text-neutral-600 uppercase tracking-wider">Profissional</span>
              </div>
              <div className="aspect-[4/5] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src={IMAGES.worker} 
                  alt="Professional contractor" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-4 sm:-bottom-6 left-2 sm:-left-6 md:-left-12 bg-white rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-6 max-w-[200px] sm:max-w-xs">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-neutral-900 text-xs sm:text-base">Pagamento Liberado</p>
                    <p className="text-[10px] sm:text-xs text-neutral-500">Serviço aprovado pelo cliente</p>
                  </div>
                </div>
                <div className="h-1.5 sm:h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full w-full bg-green-500 rounded-full" />
                </div>
                <p className="text-[10px] sm:text-xs text-neutral-500 mt-1 sm:mt-2">R$ 2.500,00 transferido para sua conta</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section id="testimonials" className="py-16 sm:py-24 md:py-32 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-white/10 rounded-full text-[10px] sm:text-xs font-semibold text-white/70 uppercase tracking-wider mb-3 sm:mb-4">
              Depoimentos
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3 sm:mb-4">
              O que dizem sobre nós
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-white/60 max-w-2xl mx-auto">
              Milhares de clientes e profissionais já transformaram a forma de contratar serviços
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {TESTIMONIALS.map((testimonial, i) => (
              <div 
                key={i}
                className="p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-0.5 sm:gap-1 mb-3 sm:mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-3 h-3 sm:w-4 sm:h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-white/80 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">"{testimonial.text}"</p>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-sm sm:text-base">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm sm:text-base">{testimonial.name}</p>
                    <p className="text-xs sm:text-sm text-white/50">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CLIENT SATISFACTION SECTION ========== */}
      <section className="py-16 sm:py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left - Image */}
            <div className="relative pt-3">
              {/* Client Badge */}
              <div className="absolute top-0 left-4 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-lg border border-neutral-100 z-10">
                <span className="text-[10px] font-semibold text-neutral-600 uppercase tracking-wider">Cliente</span>
              </div>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758523418681-1b5d1532c657?auto=format&fit=crop&w=800&q=80" 
                  alt="Cliente satisfeita" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute bottom-6 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-neutral-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900 text-sm">Serviço Aprovado!</p>
                    <p className="text-xs text-neutral-500">Reforma da cozinha concluída</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs border-t border-neutral-100 pt-2 mt-2">
                  <span className="text-neutral-500">Pagamento liberado</span>
                  <span className="font-bold text-green-600">R$ 4.800,00</span>
                </div>
              </div>

              {/* Rating Badge */}
              <div className="absolute top-4 right-4 bg-white rounded-full px-4 py-2 shadow-xl flex items-center gap-2">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-neutral-900 text-sm">5.0</span>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-50 border border-green-100 rounded-full text-xs font-semibold text-green-700 uppercase tracking-wider mb-6">
                <CheckCircle2 className="w-4 h-4" />
                Satisfação Garantida
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-4 sm:mb-6">
                Serviço executado, cliente feliz, sem dor de cabeça
              </h2>
              <p className="text-base sm:text-lg text-neutral-500 mb-8 sm:mb-10">
                Na eZelo, você contrata com tranquilidade. O profissional só recebe após sua aprovação, garantindo que o serviço seja entregue exatamente como combinado.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Shield, title: "Pagamento só após aprovação", desc: "Seu dinheiro fica protegido até você confirmar que está satisfeito" },
                  { icon: FileCheck, title: "Tudo documentado", desc: "Fotos do antes e depois, cronograma e todas as conversas registradas" },
                  { icon: Star, title: "Avalie e ajude outros", desc: "Sua avaliação ajuda a comunidade a encontrar os melhores profissionais" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-green-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-0.5">{item.title}</h3>
                      <p className="text-sm text-neutral-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== EARLY DELIVERY BONUS SECTION ========== */}
      <section className="py-16 sm:py-24 md:py-32 bg-neutral-900 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full text-xs font-semibold text-white/80 uppercase tracking-wider mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Novidade exclusiva
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 sm:mb-6">
              Bônus por Entrega Antecipada
            </h2>
            <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
              Incentive seu profissional! Quando o serviço é entregue antes do prazo, ele recebe um bônus definido por você.
            </p>
          </div>

          {/* Steps - Horizontal on desktop, vertical on mobile */}
          <div className="flex flex-col md:flex-row items-stretch gap-4 sm:gap-6 mb-12 sm:mb-16">
            {[
              { num: "01", title: "Defina o bônus", desc: "Ao criar o contrato, você escolhe o valor do incentivo" },
              { num: "02", title: "Profissional motivado", desc: "Ele trabalha com mais empenho para entregar antes" },
              { num: "03", title: "Todos ganham", desc: "Você recebe antes, ele ganha mais. Win-win." }
            ].map((step, i) => (
              <div key={i} className="flex-1 relative">
                <div className="h-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-3xl sm:text-4xl font-bold text-emerald-400">{step.num}</span>
                    {i < 2 && (
                      <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/20 to-transparent" />
                    )}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-1">{step.title}</h3>
                  <p className="text-sm text-white/50">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Example Card - More refined */}
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-neutral-900 flex items-center justify-center">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-neutral-900 text-sm sm:text-base">Exemplo prático</h3>
                    <p className="text-xs sm:text-sm text-neutral-500">Reforma de banheiro</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Simulação</span>
              </div>
              
              {/* Timeline visual */}
              <div className="relative mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-neutral-500">Início</span>
                  <span className="text-xs text-neutral-500">Prazo: 15 dias</span>
                </div>
                <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full w-[73%] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    <span className="text-xs font-medium text-emerald-600">Entregue dia 11</span>
                  </div>
                  <span className="text-xs font-medium text-neutral-400">4 dias antes!</span>
                </div>
              </div>

              {/* Values */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                <div className="bg-neutral-50 rounded-xl p-3 sm:p-4">
                  <p className="text-xs text-neutral-500 mb-1">Valor do serviço</p>
                  <p className="text-lg sm:text-xl font-bold text-neutral-900">R$ 4.500</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 sm:p-4 border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-1">Bônus antecipação</p>
                  <p className="text-lg sm:text-xl font-bold text-emerald-600">+ R$ 300</p>
                </div>
              </div>

              {/* Result */}
              <div className="flex items-center justify-between p-3 sm:p-4 bg-neutral-900 rounded-xl">
                <div className="flex items-center gap-2 sm:gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-medium text-sm sm:text-base">Total recebido pelo profissional</span>
                </div>
                <span className="text-lg sm:text-xl font-bold text-white">R$ 4.800</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PROFESSIONALS SECTION ========== */}
      <section id="professionals" className="py-16 sm:py-24 md:py-32 bg-neutral-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left - Content */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-400/10 border border-yellow-400/20 rounded-full text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-6">
                <Users className="w-4 h-4" />
                Para Profissionais
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 sm:mb-6">
                É profissional?{" "}
                <span className="text-yellow-400">Cresça com a eZelo.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/70 mb-8 sm:mb-10">
                Cadastre-se gratuitamente e tenha acesso a milhares de clientes que buscam serviços de qualidade. Receba pagamentos garantidos e construa sua reputação.
              </p>

              <div className="space-y-4 mb-8 sm:mb-10">
                {[
                  { icon: DollarSign, title: "Pagamento Garantido", desc: "Receba pelo seu trabalho com segurança via Conta Garantia" },
                  { icon: Users, title: "Mais Clientes", desc: "Acesse uma base de clientes em crescimento constante" },
                  { icon: Star, title: "Construa Reputação", desc: "Avaliações positivas aumentam sua visibilidade" },
                  { icon: Shield, title: "Contrato Protegido", desc: "Trabalhe com respaldo jurídico em todos os serviços" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-yellow-400/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-white/60">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a 
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-yellow-400 text-neutral-900 hover:bg-yellow-300 text-sm sm:text-base font-bold inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="pro-cta-ios"
                >
                  Cadastre-se Grátis
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <a 
                  href="#how-it-works"
                  className="h-12 sm:h-14 px-6 sm:px-8 rounded-full border-2 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base font-medium inline-flex items-center justify-center gap-2 transition-colors"
                >
                  Saiba mais
                </a>
              </div>
            </div>

            {/* Right - Phone Mockup for Professional */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Phone Frame */}
                <div className="w-56 sm:w-72 h-[460px] sm:h-[580px] bg-neutral-800 rounded-[2.5rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl shadow-black/50 border border-white/10">
                  <div className="w-full h-full bg-white rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="h-10 sm:h-12 bg-neutral-900 flex items-center justify-center">
                      <div className="w-20 sm:w-24 h-5 sm:h-6 bg-neutral-800 rounded-full" />
                    </div>
                    {/* App Content Preview - Professional View */}
                    <div className="p-3 sm:p-5">
                      <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <div>
                          <p className="text-sm sm:text-lg font-bold text-neutral-900 tracking-tight">eZelo.</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Olá, Carlos</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                          {/* Notification Icon */}
                          <div className="relative">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                            </svg>
                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></div>
                          </div>
                          {/* Avatar */}
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-yellow-400">
                            <img 
                              src="https://images.unsplash.com/photo-1732660780054-0cf9fadb9d30?w=100&h=100&fit=crop&crop=face" 
                              alt="Carlos" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Earnings Card */}
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-5">
                        <p className="text-neutral-900/60 text-[10px] sm:text-xs font-medium">Ganhos este mês</p>
                        <p className="text-xl sm:text-3xl font-bold text-neutral-900">R$ 4.850</p>
                        <div className="flex items-center gap-1 mt-1">
                          <svg className="w-3 h-3 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                          <span className="text-[9px] sm:text-[10px] text-green-700 font-medium">+23% vs mês anterior</span>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="flex gap-1.5 sm:gap-2 mb-3 sm:mb-5">
                        <div className="flex-1 bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-1.5 sm:p-2 text-center">
                          <p className="text-sm sm:text-lg font-bold text-neutral-900">5</p>
                          <p className="text-[6px] sm:text-[8px] text-neutral-500 uppercase tracking-wider">Pendentes</p>
                        </div>
                        <div className="flex-1 bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-1.5 sm:p-2 text-center">
                          <p className="text-sm sm:text-lg font-bold text-neutral-900">12</p>
                          <p className="text-[6px] sm:text-[8px] text-neutral-500 uppercase tracking-wider">Concluídos</p>
                        </div>
                        <div className="flex-1 bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-1.5 sm:p-2 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400" viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            <span className="text-sm sm:text-lg font-bold text-neutral-900">4.9</span>
                          </div>
                          <p className="text-[6px] sm:text-[8px] text-neutral-500 uppercase tracking-wider">Nota</p>
                        </div>
                      </div>

                      {/* New Request */}
                      <p className="text-[8px] sm:text-[10px] text-neutral-400 uppercase tracking-wider mb-2">Nova Solicitação</p>
                      <div className="bg-green-50 border border-green-100 rounded-xl sm:rounded-2xl p-2.5 sm:p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M12 12v7M9 19h6" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-[10px] sm:text-xs font-semibold text-neutral-900">Limpeza Residencial</p>
                            <p className="text-[8px] sm:text-[10px] text-neutral-500">Maria Silva • 2.3km</p>
                          </div>
                        </div>
                        <div className="text-center mb-2">
                          <span className="text-sm sm:text-base font-bold text-green-600">R$ 180</span>
                        </div>
                        <div className="flex gap-1.5 sm:gap-2">
                          <button className="flex-1 bg-green-500 text-white text-[9px] sm:text-[10px] font-semibold py-1.5 sm:py-2 rounded-lg">Aceitar</button>
                          <button className="flex-1 bg-neutral-200 text-neutral-600 text-[9px] sm:text-[10px] font-semibold py-1.5 sm:py-2 rounded-lg">Recusar</button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom Navigation Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-14 bg-white border-t border-neutral-100 flex items-center justify-around px-2">
                      {/* Home - Active */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9.5L12 4l9 5.5M4 10v9a1 1 0 001 1h4v-5h6v5h4a1 1 0 001-1v-9" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-900 font-medium mt-0.5">Início</span>
                      </div>
                      {/* Jobs */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Serviços</span>
                      </div>
                      {/* Earnings */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Ganhos</span>
                      </div>
                      {/* Profile */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Perfil</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -bottom-3 sm:-bottom-4 -right-3 sm:-right-4 bg-yellow-400 text-neutral-900 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold text-xs sm:text-sm shadow-lg">
                  Grátis!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ALREADY HAVE A PROFESSIONAL SECTION ========== */}
      <section className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-white to-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left - Visual */}
            <div className="order-2 lg:order-1">
              <div className="relative max-w-md mx-auto">
                {/* Main Image */}
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=800&q=80" 
                    alt="Profissional de confiança" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                
                {/* Contract Preview Card */}
                <div className="absolute bottom-6 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-neutral-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center">
                        <FileCheck className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-neutral-900 text-sm">Contrato Ativo</p>
                        <p className="text-xs text-neutral-500">Manutenção Elétrica</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-full">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      <span className="text-xs font-semibold text-green-700">Protegido</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-neutral-500">Valor acordado</span>
                    <span className="font-bold text-neutral-900">R$ 450,00</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-green-500 rounded-full" />
                  </div>
                  <p className="text-[10px] text-neutral-400 mt-1">75% do serviço concluído</p>
                </div>

                {/* Floating Badge - Top Right */}
                <div className="absolute -top-3 -right-3 bg-neutral-900 text-white rounded-2xl px-4 py-2 shadow-xl">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-semibold">Conta Garantia Ativa</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-50 border border-green-100 rounded-full text-xs font-semibold text-green-700 uppercase tracking-wider mb-6">
                <CheckCircle2 className="w-4 h-4" />
                Já tem um profissional?
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-4 sm:mb-6">
                Formalize e proteja sua relação de trabalho
              </h2>
              <p className="text-base sm:text-lg text-neutral-500 mb-8 sm:mb-10">
                Você já tem um eletricista, encanador ou diarista de confiança? Ótimo! Use a eZelo para ter contratos válidos, pagamentos seguros e histórico documentado.
              </p>

              <div className="space-y-4 mb-8 sm:mb-10">
                {[
                  { icon: FileCheck, title: "Contrato com Validade Jurídica", desc: "Transforme acordos verbais em contratos digitais protegidos por lei" },
                  { icon: Shield, title: "Pagamento Protegido", desc: "Seu dinheiro fica na Conta Garantia até você aprovar o serviço realizado" },
                  { icon: Clock, title: "Histórico Completo", desc: "Registro de todos os serviços, valores e avaliações em um só lugar" },
                  { icon: Bot, title: "Assistente IA para Orçamentos", desc: "Receba sugestões de valores justos baseados no mercado" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-neutral-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-0.5">{item.title}</h3>
                      <p className="text-sm text-neutral-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a 
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 text-sm sm:text-base font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="existing-pro-cta"
                >
                  Cadastrar meu profissional
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== APP DOWNLOAD SECTION ========== */}
      <section id="app" className="py-16 sm:py-24 md:py-32 bg-gradient-to-br from-neutral-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left - Content */}
            <div>
              <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1 sm:py-1.5 bg-neutral-100 rounded-full text-[10px] sm:text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-4 sm:mb-6">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse" />
                Disponível agora
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-4 sm:mb-6">
                eZelo. no seu celular
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-neutral-500 mb-6 sm:mb-8">
                Gerencie seus serviços, acompanhe contratos e converse com o assistente IA direto do seu smartphone.
              </p>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-10">
                {[
                  "Notificações em tempo real",
                  "Chat com profissionais",
                  "Assistente IA sempre disponível",
                  "Assinatura digital pelo celular",
                  "Pagamentos seguros"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    </div>
                    <span className="text-neutral-700 text-sm sm:text-base">{feature}</span>
                  </div>
                ))}
              </div>

              {/* App Store Badges */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a 
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:gap-3 bg-neutral-900 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl cursor-pointer hover:bg-neutral-800 transition-colors w-full sm:w-52 justify-center"
                >
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[9px] sm:text-[10px] text-white/60 uppercase tracking-wider">Baixar na</p>
                    <p className="text-base sm:text-lg font-semibold -mt-0.5">App Store</p>
                  </div>
                </a>
                <a 
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:gap-3 bg-neutral-900 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl cursor-pointer hover:bg-neutral-800 transition-colors w-full sm:w-52 justify-center"
                >
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[9px] sm:text-[10px] text-white/60 uppercase tracking-wider">Baixar no</p>
                    <p className="text-base sm:text-lg font-semibold -mt-0.5">Google Play</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right - Phone Mockup */}
            <div className="flex justify-center mt-8 lg:mt-0">
              <div className="relative">
                {/* Phone Frame */}
                <div className="w-56 sm:w-72 h-[460px] sm:h-[580px] bg-neutral-900 rounded-[2.5rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl shadow-neutral-900/30">
                  <div className="w-full h-full bg-white rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="h-10 sm:h-12 bg-neutral-900 flex items-center justify-center">
                      <div className="w-20 sm:w-24 h-5 sm:h-6 bg-neutral-800 rounded-full" />
                    </div>
                    {/* App Content Preview */}
                    <div className="p-3 sm:p-5">
                      <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <div>
                          <p className="text-sm sm:text-lg font-bold text-neutral-900 tracking-tight">eZelo.</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Olá, João</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                          {/* Notification Icon */}
                          <div className="relative">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                            </svg>
                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></div>
                          </div>
                          {/* Avatar */}
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-neutral-200">
                            <img 
                              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" 
                              alt="João" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-5">
                        <div className="flex-1 bg-neutral-50 border border-neutral-100 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center">
                          <p className="text-lg sm:text-2xl font-bold text-neutral-900">3</p>
                          <p className="text-[8px] sm:text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5 sm:mt-1">Serviços</p>
                        </div>
                        <div className="flex-1 bg-neutral-50 border border-neutral-100 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center">
                          <p className="text-lg sm:text-2xl font-bold text-neutral-900">2</p>
                          <p className="text-[8px] sm:text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5 sm:mt-1">Contratos</p>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="bg-neutral-900 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-5 flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold text-xs sm:text-sm">Novo Serviço</p>
                          <p className="text-neutral-400 text-[10px] sm:text-xs">Solicite um orçamento</p>
                        </div>
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white/10 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                        </div>
                      </div>

                      {/* Categories - Elegant SVG Icons */}
                      <p className="text-[8px] sm:text-[10px] text-neutral-400 uppercase tracking-wider mb-2 sm:mb-3">Categorias</p>
                      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                        {/* Limpeza */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M12 12v7M9 19h6" strokeLinecap="round" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Limpeza</p>
                        </div>
                        {/* Jardim */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path d="M12 19V11M12 11C12 11 8 8 8 5C8 2 12 2 12 2C12 2 16 2 16 5C16 8 12 11 12 11Z" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M7 15C5 13 5 10 7 8M17 15C19 13 19 10 17 8" strokeLinecap="round" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Jardim</p>
                        </div>
                        {/* Piscina */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path d="M2 12C4 10 6 14 8 12C10 10 12 14 14 12C16 10 18 14 20 12" strokeLinecap="round" />
                              <path d="M2 17C4 15 6 19 8 17C10 15 12 19 14 17C16 15 18 19 20 17" strokeLinecap="round" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Piscina</p>
                        </div>
                        {/* Clima */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <rect x="3" y="5" width="18" height="10" rx="2" />
                              <path d="M7 18v2M12 18v1M17 18v2" strokeLinecap="round" />
                              <path d="M7 9h10" strokeLinecap="round" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Clima</p>
                        </div>
                        {/* Reforma */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Reforma</p>
                        </div>
                        {/* Outros */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-0.5 sm:mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <circle cx="12" cy="12" r="1" fill="currentColor" />
                              <circle cx="6" cy="12" r="1" fill="currentColor" />
                              <circle cx="18" cy="12" r="1" fill="currentColor" />
                            </svg>
                          </div>
                          <p className="text-[8px] sm:text-[9px] text-neutral-600">Outros</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom Navigation Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-14 bg-white border-t border-neutral-100 flex items-center justify-around px-2">
                      {/* Home - Active */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9.5L12 4l9 5.5M4 10v9a1 1 0 001 1h4v-5h6v5h4a1 1 0 001-1v-9" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-900 font-medium mt-0.5">Início</span>
                      </div>
                      {/* Search */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Buscar</span>
                      </div>
                      {/* Contracts */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Contratos</span>
                      </div>
                      {/* Profile */}
                      <div className="flex flex-col items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        <span className="text-[6px] sm:text-[7px] text-neutral-400 mt-0.5">Perfil</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -bottom-3 sm:-bottom-4 -right-3 sm:-right-4 bg-green-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold text-xs sm:text-sm shadow-lg">
                  Grátis!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-16 sm:py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.pool}
            alt="Luxury pool"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-4 sm:mb-6">
            Baixe agora e contrate com segurança
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-neutral-500 mb-8 sm:mb-10 max-w-2xl mx-auto">
            Junte-se a milhares de pessoas que já descobriram uma forma mais segura de contratar serviços residenciais.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a 
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 sm:h-14 px-6 sm:px-10 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 text-sm sm:text-base font-semibold shadow-2xl shadow-neutral-900/20 inline-flex items-center justify-center gap-2 sm:gap-3"
              data-testid="final-cta-ios"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              App Store
            </a>
            <a 
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 sm:h-14 px-6 sm:px-10 rounded-full border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm sm:text-base font-medium inline-flex items-center justify-center gap-2 sm:gap-3"
              data-testid="final-cta-android"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Google Play
            </a>
          </div>

          <p className="text-xs sm:text-sm text-neutral-400 mt-6 sm:mt-8">
            100% gratuito • Sem taxa de adesão • Disponível para iOS e Android
          </p>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-10 sm:py-16 bg-gradient-to-b from-neutral-100 to-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10 mb-8 sm:mb-10">
            <div className="col-span-2 md:col-span-1">
              <span className="text-lg sm:text-xl font-bold text-neutral-900">eZelo.</span>
              <p className="text-neutral-500 text-xs sm:text-sm mt-2 sm:mt-3">
                A plataforma mais segura para contratar serviços residenciais no Brasil.
              </p>
            </div>
            <div>
              <p className="font-semibold text-neutral-900 mb-2 sm:mb-4 text-sm sm:text-base">Serviços</p>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-neutral-500">
                <li>Limpeza</li>
                <li>Jardinagem</li>
                <li>Piscina</li>
                <li>Ar-Condicionado</li>
                <li>E muito mais...</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-neutral-900 mb-2 sm:mb-4 text-sm sm:text-base">Empresa</p>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-neutral-500">
                <li>Sobre nós</li>
                <li>Como funciona</li>
                <li>Seja um parceiro</li>
                <li>Contato</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-neutral-900 mb-2 sm:mb-4 text-sm sm:text-base">Legal</p>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-neutral-500">
                <li>Termos de uso</li>
                <li>Política de privacidade</li>
                <li>Política de Pagamento Protegido</li>
              </ul>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between pt-6 sm:pt-8 border-t border-neutral-200/60 gap-4">
            <p className="text-xs sm:text-sm text-neutral-500 text-center md:text-left">© 2026 eZelo. Todos os direitos reservados.</p>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-neutral-500">
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Ambiente Seguro</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-neutral-500">
                <FileCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Contratos Válidos</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes scroll-down {
          0%, 100% { 
            opacity: 1;
            transform: translateY(0); 
          }
          50% { 
            opacity: 0.5;
            transform: translateY(6px); 
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .animation-delay-100 {
          animation-delay: 0.1s;
          opacity: 0;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
        
        .animation-delay-300 {
          animation-delay: 0.3s;
          opacity: 0;
        }
        
        .animate-scroll-down {
          animation: scroll-down 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
